import React, { useState, useEffect } from 'react';
import "./FinancialProcess.css"
const FinancialProcessUI = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.2 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      number: "01",
      title: "Consultation",
      subtitle: "Understand Your Financial Needs & Goals",
      icon: "💬",
      color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      details: [
        "Discuss your financial situation, income, expenses, and lifestyle",
        "Understand your short-term and long-term goals (retirement, investments, education)",
        "Identify challenges like debt management, tax burden, or lack of savings",
        "Provide initial guidance and answer your key questions"
      ],
      outcome: "The consultation helps us build a clear picture of your financial journey."
    },
    {
      number: "02",
      title: "Planning",
      subtitle: "Create a Personalized Strategy",
      icon: "🎯",
      color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      details: [
        "Analyze your current financial health using professional tools",
        "Design a customized financial strategy based on your goals",
        "Suggest best investment options, tax-saving methods, and risk management",
        "Share a step-by-step roadmap that ensures your money works for you"
      ],
      outcome: "The planning stage focuses on making your dreams achievable with realistic, practical solutions."
    },
    {
      number: "03",
      title: "Execution & Support",
      subtitle: "Implement Solutions with Ongoing Guidance",
      icon: "🚀",
      color: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      details: [
        "Help you set up investments, insurance, and other financial instruments",
        "Regularly track your progress and adjust strategies if needed",
        "Provide continuous support and updates on market trends",
        "Offer annual reviews to keep you on track with your goals"
      ],
      outcome: "This step ensures you don't just have a plan, but also expert support to keep your finances on the right path."
    }
  ];

  return (
    <div style={{
      background: '#ffffff',
      marginTop : " 70px",
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0.1,
        pointerEvents: 'none'
      }}>
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: Math.random() * 4 + 2 + 'px',
              height: Math.random() * 4 + 2 + 'px',
              background: '#333',
              borderRadius: '50%',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              animation: `float ${Math.random() * 3 + 2}s ease-in-out infinite alternate`
            }}
          />
        ))}
      </div>
      <div className="container-fluid px-0">
        {/* Header Section */}
        <div 
          className="text-center py-3"
          id="header"
          data-animate
          style={{
            animation: isVisible.header ? 'slideInUp 1s ease-out' : 'none',
            opacity: isVisible.header ? 1 : 0
          }}
        >
          <h1 style={{
            fontSize: 'clamp(2.5rem, 3vw, 3rem)',
            fontWeight: '700',
            color: '#333',
            marginBottom: '0.5rem'
          }}>
            Our Simple 3-Step Process
          </h1>
          <p style={{
            fontSize: '1.5rem',
            color: 'rgba(0, 0, 0, 0.7)',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.5',
            padding: '0 20px'
          }}>
            Simple, transparent, and result-driven process to understand your needs and create the right plan.
          </p>
        </div>

        {/* Process Section */}
        <div style={{
          position: 'relative',
          padding: '30px 0'
        }}>
          {/* Process Steps */}
          <div className="row justify-content-center align-items-center" style={{ padding: '0 20px' }}>
            {steps.map((step, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-3" style={{ padding: '0 15px' }}>
                <div
                  className="step-item"
                  id={`step-${index}`}
                  data-animate
                  style={{
                    position: 'relative',
                    zIndex: 2,
                    animation: isVisible[`step-${index}`] ? `slideInUp 0.8s ease-out ${index * 0.2}s both` : 'none',
                    opacity: isVisible[`step-${index}`] ? 1 : 0
                  }}
                >
                  {/* Step Circle */}
                  <div
                    style={{
                      width: '80px',
                      height: '80px',
                      background: step.color,
                      borderRadius: '50%',
                      margin: '0 auto 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                      animation: activeStep === index ? 'pulse 2s infinite' : 'none'
                    }}
                    onClick={() => setActiveStep(activeStep === index ? -1 : index)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 15px 30px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                    }}
                  >
                    {/* Step Number */}
                    <div style={{
                      position: 'absolute',
                      top: '-10px',
                      right: '-10px',
                      width: '30px',
                      height: '30px',
                      background: '#fff',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#333',
                      boxShadow: '0 3px 10px rgba(0,0,0,0.2)'
                    }}>
                      {step.number}
                    </div>
                    
                    {/* Icon */}
                    <span style={{ fontSize: '2rem' }}>
                      {step.icon}
                    </span>
                  </div>

                  {/* Step Content */}
                  <div
                    className="step-content"
                    style={{
                      background: 'rgba(0, 0, 0, 0.05)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '15px',
                      padding: '20px',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.08)'
                    }}
                  >
                    <h3 style={{
                      color: '#333',
                      fontSize: '3rem',
                      fontWeight: '600',
                      marginBottom: '8px',
                      textAlign: 'center'
                    }}>
                      {step.title}
                    </h3>
                    
                    <p style={{
                      color: 'rgba(0, 0, 0, 0.7)',
                      fontSize: '1.5rem',
                      marginBottom: '15px',
                      textAlign: 'center',
                      lineHeight: '1.4'
                    }}>
                      {step.subtitle}
                    </p>

                    {/* Expandable Details */}
                    <div style={{
                      maxHeight: activeStep === index ? '400px' : '0',
                      overflow: 'hidden',
                      transition: 'max-height 0.5s ease-in-out',
                      opacity: activeStep === index ? 1 : 0,
                      transform: activeStep === index ? 'translateY(0)' : 'translateY(-20px)'
                    }}>
                      <ul style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: '15px 0'
                      }}>
                        {step.details.map((detail, detailIndex) => (
                          <li
                            key={detailIndex}
                            style={{
                              color: 'rgba(0, 0, 0, 0.8)',
                              fontSize: '1.2rem',
                              margin: '8px 0',
                              paddingLeft: '15px',
                              position: 'relative',
                              lineHeight: '1.3'
                            }}
                          >
                            <span style={{
                              position: 'absolute',
                              left: '0',
                              top: '0',
                              width: '4px',
                              height: '4px',
                              background: step.color,
                              borderRadius: '50%',
                              marginTop: '6px'
                            }} />
                            {detail}
                          </li>
                        ))}
                      </ul>
                      
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.05)',
                        borderRadius: '8px',
                        padding: '12px',
                        marginTop: '15px',
                        borderLeft: `3px solid`,
                        borderImage: step.color + ' 1'
                      }}>
                        <p style={{
                          color: '#333',
                          fontSize: '1rem',
                          margin: 0,
                          fontStyle: 'italic'
                        }}>
                          👉 {step.outcome}
                        </p>
                      </div>
                    </div>

                    {/* Click to expand indicator */}
                    <div style={{
                      textAlign: 'center',
                      marginTop: '10px',
                      
                    }}>
                      <span style={{
                        color: 'rgba(0, 0, 0, 0.5)',
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        fontSize : "1rem"
                      }}>
                        {activeStep === index ? 'Click to collapse ↑' : 'Click to expand ↓'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialProcessUI;